import { CdkMenu } from '@angular/cdk/menu';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
} from '@angular/core';

import { MdwCollapse } from '@spaces-ui/animations/angular-css';
import { MdwIcon } from '@spaces-ui/meadow/icons';
import { Calendar_Fill, Time_Fill } from '@spaces-ui/meadow/icons/svgs';
import { Time } from '@spaces-ui/utils';

import { MdwCalendar } from '../calendar/calendar';
import { MdwTimeInput } from '../time-input/time-input';

@Component({
  selector: 'mdw-date-time-picker-card',
  templateUrl: './date-time-picker-card.html',
  styleUrls: ['./date-time-picker-card.scss'],
  imports: [MdwCalendar, MdwCollapse, MdwIcon, MdwTimeInput],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.standalone]': '!hostedInMenu',
    '(focusout)': 'onFocusOut($event)',
  },
})
export class MdwDateTimePickerCard {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  protected hostedInMenu = !!inject(CdkMenu, { optional: true });

  public date = model<Date | null>(null);
  public time = model<Time | null>(null);
  public dateEnabled = model<boolean>(true);
  public timeEnabled = model<boolean>(false);

  public filterType = input<'future' | 'past' | 'none'>('none');
  public dateLabel = input<string>('Date');
  public timeLabel = input<string>('Time');

  // Fires every time the host's box size changes (e.g. while the date or time
  // section animates open/closed). Consumers that host the card inside an
  // overlay listen for this to reposition the overlay so it stays on-screen.
  public sizeChanged = output<void>();

  protected readonly icons = { Calendar_Fill, Time_Fill };

  protected startAt = computed(() => this.date() ?? new Date());

  public constructor() {
    // One-shot reconciliation: if a non-null value is provided on init,
    // ensure the matching toggle is on. A null date is left null — the
    // calendar still opens to the current month via `startAt`, but no cell
    // is selected until the user picks one. Auto-selecting today here would
    // commit a date the user never chose when the card is hosted in an
    // overlay that opens on hover.
    let reconciled = false;
    effect(() => {
      const date = this.date();
      const time = this.time();
      if (reconciled) {
        return;
      }
      reconciled = true;
      if (date !== null) {
        this.dateEnabled.set(true);
      }
      if (time !== null) {
        this.dateEnabled.set(true);
        this.timeEnabled.set(true);
      }
    });

    afterNextRender(() => {
      const observer = new ResizeObserver(() => this.sizeChanged.emit());
      observer.observe(this.host.nativeElement);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  protected onFocusOut(event: FocusEvent): void {
    // Switching calendar views (e.g. selecting a year/month) destroys the old view and
    // removes the focused cell, so focus briefly drops to <body> — a focusout with a null
    // relatedTarget — until Material restores it asynchronously via afterNextRender. A
    // host cdkMenu closes whenever focus leaves it (its host listens for focusout and calls
    // menuStack.closeAll()), so that momentary gap would dismiss the menu mid-interaction.
    // Unlike the mat-datepicker overlay used by MdwDatePicker, cdkMenu has no tolerance for
    // this. Stop the transient internal focusout from bubbling to the cdkMenu host so it
    // never sees focus "leave"; Material re-focuses the active cell on the next render.
    // A genuine focus move out of the card carries a non-null relatedTarget and is left to
    // close the menu as normal; clicking outside still closes it via the overlay's own
    // outside-pointer handling.
    if (!this.hostedInMenu || event.relatedTarget !== null) {
      return;
    }
    event.stopPropagation();
  }

  protected onTimeChange(value: Time | undefined): void {
    this.time.set(value ?? null);
  }

  protected toggleDate(): void {
    const next = !this.dateEnabled();
    this.dateEnabled.set(next);
    if (!next) {
      this.timeEnabled.set(false);
      this.date.set(null);
      this.time.set(null);
    } else if (this.date() === null) {
      this.date.set(new Date());
    }
  }

  protected toggleTime(): void {
    const next = !this.timeEnabled();
    this.timeEnabled.set(next);
    if (!next) {
      this.time.set(null);
    }
  }
}
